import { queryOptions } from '@tanstack/react-query';
import type { DamageListSuccessResponseType, DamageQueryParams } from '@/common/damage';
import { axios } from '@/lib/utils/axios';

import { getResponse } from '../common';
import { API_URLS } from '../query.client';

export async function getListDamages(query: DamageQueryParams): Promise<DamageListSuccessResponseType | null> {
	const response = await axios.get(`${API_URLS.DAMAGES.INDEX}`, {
		params: query,
	});
	return getResponse<DamageListSuccessResponseType>(response.data);
}

/**
 * Fetches a list of damages based on the provided query parameters.
 * The query parameters can include pagination, filtering by material, category, asset, inspection, and status IDs.
 * The query will only be enabled if the enabled flag is true.
 *
 * @param query - An object containing query parameters for filtering and pagination.
 * @param enabled - A boolean flag to enable or disable the query (default: true).
 * @returns A React Query options object for fetching the list of damages.
 * @example
 * const { data, isLoading } = useQuery(getListDamagesOptions({ limit: 10, offset: 0 }));
*/
export function getListDamagesOptions(query: DamageQueryParams, enabled = true) {
	return queryOptions({
		queryKey: ['damages', 'list', query],
		queryFn: () => getListDamages(query),
		enabled,
	});
}

/**
 * Fetches a list of damages for a specific inspection with the given query parameters.
 * The query parameters can include pagination, filtering by material, category, asset, and status IDs.
 * The query will only be enabled if the inspectionId is provided and the enabled flag is true.
 *
 * @param inspectionId - The ID of the inspection to fetch damages for.
 * @param query - An object containing query parameters for filtering and pagination.
 * @param enabled - A boolean flag to enable or disable the query (default: true).
 * @returns A React Query options object for fetching the list of damages.
 * @example
 * const { data, isLoading } = useQuery(getListDamagesByInspectionOptions('inspection123', { limit: 10, offset: 0 }));
*/
export function getListDamagesByInspectionOptions(inspectionId: string, query: DamageQueryParams, enabled = true) {
	return queryOptions({
		queryKey: ['damages', 'list', 'inspection', inspectionId, query],
		queryFn: () => getListDamages({ ...query, inspectionId }),
		enabled: !!inspectionId && enabled,
	});
}
